import React from "react";
import { Divider, Modal, QRCode } from "antd";
import ButtonUI from "../Button";
import TypographyUI from "../Typography";
import { useDispatch } from "react-redux";
import useCommonSettings from "@base/hooks/useCommonSettings";
import CommonIcons from "@base/assets/icons";
import { useTranslation } from "react-i18next";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";

export default function TransferInformationDialog({
  open = false,
  toggle,
  transferContent = "",
  resultOrder,
}: {
  open: boolean;
  toggle: () => void;
  transferContent?: string;
  resultOrder?: any;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { hotline, bank } = useCommonSettings();
  const orderVa = resultOrder?.order_va;

  const BANK_NAME = orderVa?.bank_name || bank.name || "-";
  const BANK_NUMBER = orderVa?.va_number || bank.number || "-";
  const BANK_RECEIVER = orderVa?.va_name || bank.receiver || "-";
  // The order's own QR arrives as bare base64; the default one is an image link.
  const BANK_RECEIVER_QR = orderVa?.qr
    ? /^(data:|https?:)/.test(orderVa.qr)
      ? orderVa.qr
      : `data:image/png;base64,${orderVa.qr}`
    : bank.qr;

  const onCopyRefCode = (value?: string) => {
    navigator.clipboard.writeText(value ?? "");

    dispatch(setAlertMessage({ type: "success", message: t("Notification"), description: t("Copied") + "!" }));
  };

  return (
    <Modal
      width={993}
      open={open}
      centered
      closable
      onCancel={toggle}
      title={
        <div className="w-full flex justify-center">
          <TypographyUI use="paragraph" className="!text-lg lg:!text-2xl font-medium text-common-maintext">
            Chuyển khoản ngân hàng
          </TypographyUI>
        </div>
      }
      footer={
        <div className="w-full flex justify-center">
          <ButtonUI className="h-12 text-base font-medium" onClick={toggle}>
            Đóng
          </ButtonUI>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 w-full flex justify-center">
          {BANK_RECEIVER_QR ? <img src={BANK_RECEIVER_QR} alt="" className="w-full max-lg:max-w-[350px]" /> : null}
        </div>

        <div className="lg:col-span-3 flex flex-col gap-3">
          <div className="flex gap-2 max-md:flex-col">
            <TypographyUI use="text" className="flex-none !text-sm lg:!text-base !font-normal !text-common-subtext !mb-0 inline !w-[200px]">
              Ngân hàng:
            </TypographyUI>
            <TypographyUI use="paragraph" className="grow !text-sm lg:!text-base !font-medium !text-common-maintext !mb-0">
              {BANK_NAME}
            </TypographyUI>
          </div>
          <div className="flex gap-2 max-md:flex-col">
            <TypographyUI use="text" className="flex-none !text-sm lg:!text-base !font-normal !text-common-subtext !mb-0 inline !w-[200px]">
              Số tài khoản:
            </TypographyUI>
            <TypographyUI use="paragraph" className="grow !text-sm lg:!text-base !font-medium !text-common-maintext !mb-0">
              {BANK_NUMBER}

              {BANK_NUMBER ? (
                <div
                  className="flex items-center gap-2 text-sm font-medium font-['Inter'] text-common-primary cursor-pointer"
                  onClick={() => onCopyRefCode(BANK_NUMBER)}
                >
                  <CommonIcons.IconCopy /> {t("copy")}
                </div>
              ) : null}
            </TypographyUI>
          </div>
          <div className="flex gap-2 max-md:flex-col">
            <TypographyUI use="text" className="flex-none !text-sm lg:!text-base !font-normal !text-common-subtext !mb-0 inline !w-[200px]">
              Chủ tài khoản:
            </TypographyUI>
            <TypographyUI use="paragraph" className="grow !text-sm lg:!text-base !font-medium !text-common-maintext !mb-0">
              {BANK_RECEIVER}
            </TypographyUI>
          </div>
          <div className="flex gap-2 max-md:flex-col">
            <TypographyUI use="text" className="flex-none !text-sm lg:!text-base !font-normal !text-common-subtext !mb-0 inline !w-[200px]">
              Nội dung chuyển khoản:
            </TypographyUI>
            <TypographyUI use="paragraph" className="grow !text-sm lg:!text-base !font-medium !text-common-maintext !mb-0">
              {transferContent}

              {transferContent ? (
                <div
                  className="flex items-center gap-2 text-sm font-medium font-['Inter'] text-common-primary cursor-pointer"
                  onClick={() => onCopyRefCode(transferContent)}
                >
                  <CommonIcons.IconCopy /> {t("copy")}
                </div>
              ) : null}
            </TypographyUI>
          </div>
        </div>
      </div>

      <Divider className="my-4" />

      <div className="">
        <TypographyUI use="paragraph" className="!text-sm lg:!text-base font-medium text-common-maintext !mb-2">
          Lưu ý
        </TypographyUI>

        <ul className="list-disc ml-6">
          <li>
            <TypographyUI use="paragraph" className="!text-sm lg:!text-base font-normal text-common-subtext !mb-1">
              Hệ thống sẽ tự động xử lý giao dịch của bạn sau khi chuyển khoản
            </TypographyUI>
          </li>
          <li>
            <TypographyUI use="paragraph" className="!text-sm lg:!text-base font-normal text-common-subtext !mb-1">
              Trong trường hợp sau 5 phút không được xử lý, vui lòng liên hệ hotline: <span className="underline text-common-blue500">{hotline}</span>{" "}
              để được hỗ trợ
            </TypographyUI>
          </li>
        </ul>
      </div>
    </Modal>
  );
}
